import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./SortModal.module.css";
import ImageRadioOption from "shared/ImageRadioOption";

/**
 * SortModal
 * Scope Doc p.5 Fig 2.0.1
 *
 * A dark-blue "Sort" trigger button that opens a floating panel of radio
 * sort options. The currently selected option is shown with the active
 * (yellow) radio state. A "Close" button dismisses the panel.
 *
 * Note: the Figma dev-mode CSS for the panel's action button was labeled
 * "Share" (leftover from a copy/pasted component) but the scope doc calls
 * for a "Close" button wired to onClose, so the label/behavior below follow
 * the scope doc while the visual style (dark navy fill, white bold 13px
 * label, 32px padded square button) follows the Figma spec exactly.
 */
export function SortModal({ options, selected, onChange, onClose, container }) {
  const [isOpen, setIsOpen] = useState(false);

  // Lock page scroll and allow Escape to dismiss while the overlay is open.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const handleOptionClick = (value) => () => {
    onChange(value);
    handleClose();
  };

  return (
    <div className={styles.container}>
      <button
        type="button"
        className={styles.triggerButton}
        onClick={() => setIsOpen(true)}
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className={styles.triggerButtonText}>Sort</span>
      </button>

      {isOpen &&
        createPortal(
          // Clicking the grey backdrop closes the modal; clicks inside the
          // panel are stopped from bubbling so they don't trigger a close.
          <div className={styles.overlay} onMouseDown={handleClose}>
            <div
              className={styles.panel}
              role="dialog"
              aria-label="Sort By"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className={styles.content}>
                <div className={styles.headerRow}>
                  <span className={styles.title}>Sort By</span>
                  <div className={styles.divider} />
                </div>

                <ul className={styles.optionsList}>
                  {options.map((option) => {
                    const isActive = option.value === selected;
                    return (
                      <li
                        key={option.value}
                        className={classNames(styles.optionRow, {
                          [styles.optionRowActive]: isActive,
                        })}
                        onClick={handleOptionClick(option.value)}
                      >
                        <ImageRadioOption
                          checked={isActive}
                          label={option.label}
                          iconClassName={styles.radio}
                          labelClassName={styles.optionLabel}
                        />
                      </li>
                    );
                  })}
                </ul>
              </div>

              <button
                type="button"
                className={styles.closeButton}
                onClick={handleClose}
              >
                <span className={styles.closeButtonText}>Close</span>
              </button>
            </div>
          </div>,
          container?.current ?? document.body,
        )}
    </div>
  );
}

SortModal.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  selected: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  container: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

SortModal.defaultProps = {
  selected: null,
  onClose: undefined,
  container: null,
};

export default SortModal;
